import axios from "@/axios";
import Util from "@/util/util";
import moment from "moment";

export const namespaced = true;

const defaultData = {
  id: 0,
  requestorName: null,
  requestorUnitOffice: null,
  requestorOrganizationId: null,
  requestorSignature: {
    signatureId: 0,
    type: "TEXT",
    text: null,
    fontFamily: null,
    edit: null,
    view: null,
  },
  requestorSignatureId: 1,
  requestorDateRequested: null,
  requestorDateSigned: null,
  organizations: [],
};

const resetData = {
  type: "TEXT",
  text: null,
  fontFamily: null,
  edit: null,
  view: null,
};

const IMAGE_EXP = "data:image\/[a-z]+;base64,";

export const state = {
  authorizedRequestor: Object.assign({}, defaultData),
  helper: { countryId: 0, buySell: "" },
  submitStatus: false,
  authorizeStatus: false,
  authorizedRequestorErrors: [],
};

export const getters = {
  authorizedRequestor: (state) => state.authorizedRequestor,
  authorizedRequestorSubmitStatus: (state) => state.submitStatus,
  authorizedRequestorAuthorizeStatus: (state) => state.authorizeStatus,
  authorizedRequestorHelper: (state) => state.helper,
  authorizedRequestorErrors: (state) => state.authorizedRequestorErrors
};

export const mutations = {
  SET_REQUESTOR_API: (state, response) => {
    state.authorizedRequestor = Object.assign({}, state.authorizedRequestor, response.data, { requestorSignature: response.data.requestorSignature ? response.data.requestorSignature : defaultData.requestorSignature });

    if (state.authorizedRequestor.requestorSignature.edit && !state.authorizedRequestor.requestorSignature.edit.match(IMAGE_EXP)) {
      state.authorizedRequestor.requestorSignature.edit = window.atob(state.authorizedRequestor.requestorSignature.edit)
    }

    if (state.authorizedRequestor.requestorSignature.view && !state.authorizedRequestor.requestorSignature.view.match(IMAGE_EXP)) {
      state.authorizedRequestor.requestorSignature.view = window.atob(state.authorizedRequestor.requestorSignature.view)
    }
  },
  SET_REQUESTOR_SIG: (state, response) => {
    var requestorSig = Object.assign({}, response.data.requestorSignature ? response.data.requestorSignature : defaultData.requestorSignature);
    requestorSig.edit = window.atob(requestorSig.edit);
    requestorSig.view = window.atob(requestorSig.view);
    state.authorizedRequestor.requestorSignature = requestorSig;
  },
  SET_REQUESTOR_DATA: (state, data) => {
    state.authorizedRequestor = Object.assign({}, state.authorizedRequestor, data)

  },
  SET_SUBMIT_STATUS: (state, value) => (state.submitStatus = value),
  SET_AUTHORIZE_STATUS: (state, value) => (state.authorizeStatus = value),
  SET_HELPER: (state, value) => (state.helper = value),
  CLEAR_DATA: (state) => (state.authorizedRequestor = defaultData),
  SET_ERRORS: (state, data) => (state.authorizedRequestorErrors = data),
  CLEAR_ERRORS: (state) => (state.authorizedRequestorErrors = [])
};

export const actions = {
  clearData({ commit }) {
    commit("CLEAR_DATA");
  },

  async getAuthorizedRequestorSubmitStatus({ commit }, id) {
    if (id == 0) {
      commit("SET_SUBMIT_STATUS", false);
    }
    else {
      return await axios.get(`/api/requestorsignatures/submitstatus/${id}`)
        .then(({ data }) => {
          if (data && data.length === 0) {
            commit("SET_SUBMIT_STATUS", true);
            commit("CLEAR_ERRORS");
          }
          else {
            commit("SET_SUBMIT_STATUS", false);
            commit("SET_ERRORS", data);
          }
        });
    }
  },

  async getAuthorizedRequestorAuthorizeStatus({ commit }, id) {
    if (id == 0) {
      commit("SET_AUTHORIZE_STATUS", false);
    }
    else {
      return await axios
        .get(`/api/requestorsignatures/authorizestatus/${id}`)
        .then(({ data }) => {
          if (data && data.length === 0) {
            commit("SET_AUTHORIZE_STATUS", true);
            commit("CLEAR_ERRORS");
          } else {
            commit("SET_AUTHORIZE_STATUS", false);
            commit("SET_ERRORS", data);
          }
        });
    }
  },

  async getAuthorizedRequestorHelper({ commit }, id) {
    if (id == 0) {
      commit("SET_HELPER", { countryId: 0, buySell: "" });
    }
    else {
      return await axios.get("/api/signatures/helper/" + id)
        .then(({ data }) => {
          commit("SET_HELPER", data);
        });
    }
  },

  async getAuthorizedRequestor({ commit, state, dispatch }, orderId) {
    try {
      commit("SET_REQUESTOR_API", await axios.get(`/api/requestorsignatures/${orderId}`));
    }
    catch (error) {
      console.log("ERROR LOADING DELIVERY INFORMATION: ", error); //not handelled
    }
  },

  async getAuthorizedRequestorSigOnly({ commit, state, dispatch }, orderId) {
    try {
      commit("SET_REQUESTOR_SIG", await axios.get(`/api/requestorsignatures/${orderId}`));
    }
    catch (error) {
      console.log("ERROR LOADING DELIVERY INFORMATION: ", error); //not handelled
    }
  },

  getCacSignature({ state, dispatch }) {
    return new Promise((resolve, reject) => {
      axios.get("/api/signatures/certificatename")
        .then(function (response) {
          const cacSignature = "data:image/jpg;base64," + response.data;
          dispatch("setSignature", cacSignature);
          return resolve();
        })
        .catch(function (error) {
          return reject(`There was an error getting the CAC signature... ${error.response.data}`);
        });
    });
  },

  setSignature({ commit, state }, signature) {
    let date = null;

    if (!Util.isNullOrEmpty(signature)) {
      date = Util.convertToDateOnly(moment.utc().format("MM-DD-y HH:mm:ss"));
    }

    commit("SET_REQUESTOR_DATA", { requestorSignature: Object.assign({}, state.authorizedRequestor.requestorSignature, signature), requestorDateSigned: date });
  },

  async getOrganizations({ commit, state }, params) {
    try {
      let response = []

      if (params.buySell === "BUY") {

        response = await axios.get("/api/organizations/countrycode/US");

        let organizationId = (response && response.data) ? state.authorizedRequestor.requestorOrganizationId : null

        commit("SET_REQUESTOR_DATA", { organizations: response.data, requestorOrganizationId: organizationId });
      }

      else if (params.buySell === "SELL" && params.countryId) {

        response = await axios.get(`/api/organizations/countryid/${params.countryId}`);

        let organizationId = (response && response.data) ? state.authorizedRequestor.requestorOrganizationId : null

        commit("SET_REQUESTOR_DATA", { organizations: response.data, requestorOrganizationId: organizationId });
      }
      
      else if (params.buySell === "SELL" && !params.countryId) {
        // Fallback: Load all organizations if countryId is missing for SELL transactions
        response = await axios.get("/api/organizations");

        let organizationId = (response && response.data) ? state.authorizedRequestor.requestorOrganizationId : null

        commit("SET_REQUESTOR_DATA", { organizations: response.data, requestorOrganizationId: organizationId });
      }

      let found = response.data.find(item => item.id === state.authorizedRequestor.requestorOrganizationId);

      if (params.preOrderDetailStatus && params.agreementInformationStatus && !found) {
        commit("SET_REQUESTOR_DATA", { requestorOrganizationId: null });
      }
    }
    catch (error) {
      commit("SET_REQUESTOR_DATA", { requestorOrganizationId: null, organizations: [] });
    }
  },

  saveAuthorizedRequestor({ commit, state, dispatch }, data) {
    return new Promise((resolve, reject) => {
      let requestor = Object.assign({}, state.authorizedRequestor, {
        requestorSignature: (!state.authorizedRequestor.requestorSignature.view) ? null :
          {
            ...state.authorizedRequestor.requestorSignature,
            edit: (state.authorizedRequestor.requestorSignature.edit) ? window.btoa(state.authorizedRequestor.requestorSignature.edit) : "",
            view: (state.authorizedRequestor.requestorSignature.view) ? window.btoa(state.authorizedRequestor.requestorSignature.view) : "",
          },
       requestorDateSigned: Util.convertToDateOnly(state.authorizedRequestor.requestorDateSigned),
       pin: (state.authorizedRequestor.requestorSignature.view) ? data.pin : null 
      });

      // if it is signed, there is a pin used
      if(requestor['requestorSignature'] != null)
        requestor["pin"] = data.pin;

        axios.post("/api/requestorsignatures", requestor, { config: { showToast: false } })
        .then(function (response) {
          return dispatch("getAuthorizedRequestor", data.orderId);
        })
        .then(function () {
          return resolve();
        })
        .catch(function (error) {
          return reject(`There was an error saving the Authorized Requestor to the server...  ${error.response.data}`);
        });
    });
  },
};
